import { useState, useEffect, useMemo } from 'react';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FormInput } from 'components';
import { LoginRequest } from 'types';
import { ILoginContext } from 'context';
import { useLogin } from 'hooks';

import styles from './LoginForm.module.scss';

export function LoginForm() {
  const { t, i18n } = useTranslation('auth');
  const isRtl = i18n.dir() === 'rtl';
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { onLogin, onForgotPassword }: ILoginContext = useLogin();
  const schema = useMemo(
    () =>
      Yup.object().shape({
        email: Yup.string().email(t('validationEmailInvalid')).required(t('validationEmailRequired')),
        password: Yup.string().required(t('validationPasswordRequired')),
      }),
    [t],
  );

  const [error, setError] = useState<string | null>(null);
  const form = useForm<LoginRequest>({
    resolver: yupResolver(schema),
    defaultValues: {},
    mode: 'all',
  });

  // Chrome autofill doesn't fire React events — poll DOM to sync autofilled values into RHF
  useEffect(() => {
    const timer = setTimeout(() => {
      const emailInput = document.querySelector<HTMLInputElement>('input[name="email"]');
      const passwordInput = document.querySelector<HTMLInputElement>('input[name="password"]');
      if (emailInput?.value) form.setValue('email', emailInput.value, { shouldValidate: true });
      if (passwordInput?.value) form.setValue('password', passwordInput.value, { shouldValidate: true });
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleLogin = (): void => {
    if (form.formState.isValid) {
      onLogin(form.getValues()).catch((error) => {
        setError(error.message);
      });
    }
  };

  const handleForgotPassword = (): void => {
    onForgotPassword();
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && form.formState.isValid) {
        handleLogin();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [form.formState.isValid]);

  return (
    <div className={styles.form}>
      <Typography variant={isMobile ? 'h2' : 'h1'} component={isMobile ? 'h2' : 'h1'}>
        {t('loginTitle')}
      </Typography>

      {error && (
        <Typography variant='body1' component='span' color='#FF3D3D'>
          {error}
        </Typography>
      )}

      <Stack className={styles.row}>
        <Stack direction='row' alignItems='center'>
          <Typography component='p' variant='body2'>
            {t('emailLabel')} <span className={styles.asterisk}>*</span>
          </Typography>
        </Stack>
        <FormInput name='email' control={form.control} errorHelperClassName={styles.errorMessage} />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' alignItems='center'>
          <Typography component='p' variant='body2'>
            {t('passwordLabel')} <span className={styles.asterisk}>*</span>
          </Typography>
        </Stack>
        <FormInput
          name='password'
          control={form.control}
          errorHelperClassName={styles.errorMessage}
          type='password'
          inputProps={{ dir: 'ltr', style: { textAlign: isRtl ? 'right' : 'left' } }}
        />
      </Stack>

      <Stack direction='row' alignItems='left'>
        <Typography variant='body3' component='span' className={styles.forgotPassword} onClick={handleForgotPassword}>
          {t('forgotPassword')}
        </Typography>
      </Stack>

      <Stack className={styles.row}>
        <Button
          variant='contained'
          onClick={handleLogin}
          disabled={!form.formState.isValid}
          className={styles.formButton}
          type='submit'
        >
          {t('login')}
        </Button>
      </Stack>
    </div>
  );
}
